import { useRouter } from '@src/routes'
import { FormikErrors, useFormikContext } from 'formik'
import {
  SingleTubeFormValues,
  TubesFormValues,
  ActiveLinkType,
} from './ProductsConfiguration'

const useFormikHelper = () => {
  const formik = useFormikContext<TubesFormValues>() || {}
  const router = useRouter()

  const oligoIndex = formik.values.activeOligoIndex || 0
  const oligoFieldName = (name: string) => `oligoList.${oligoIndex}.${name}`

  const selectedOligoValues = formik.values?.oligoList?.[oligoIndex] || {}
  const touched = formik.touched?.oligoList?.[oligoIndex]
  // TODO: Figure out the correct type and why this has string | string[]
  const errors = formik.errors?.oligoList?.[oligoIndex] as
    | FormikErrors<SingleTubeFormValues>
    | undefined

  const setOligoFieldValue = (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) =>
    formik.setFieldValue(
      `oligoList.${oligoIndex}.${String(field)}`,
      value,
      shouldValidate
    )

  const setFieldTouched = (
    field: string,
    value: boolean,
    shouldValidate?: boolean
  ) =>
    formik.setFieldTouched(
      `oligoList.${oligoIndex}.${String(field)}`,
      value,
      shouldValidate
    )

  const setFieldError = (field: string, value: string) =>
    formik.setFieldError(`oligoList.${oligoIndex}.${String(field)}`, value)

  const validationFields = {
    concentration: selectedOligoValues?.concentration,
    fiveMod: selectedOligoValues?.fiveMod,
    format: selectedOligoValues?.format,
    isOverridden: !!selectedOligoValues?.isOverridden,
    name: selectedOligoValues?.name,
    otherValue: selectedOligoValues?.otherValue,
    purification: selectedOligoValues?.purification,
    scale: selectedOligoValues?.scale,
    sequence: selectedOligoValues?.sequence,
    threeMod: selectedOligoValues?.threeMod,
    tubes: selectedOligoValues?.tubes,
    amount: selectedOligoValues?.amount,
    probeType: selectedOligoValues.probeType,
    additionalServices: selectedOligoValues?.additionalServices,
    internalMod: selectedOligoValues?.internalMod,
    antisenceName: selectedOligoValues?.antisenceName,
    antisenceSequence: selectedOligoValues?.antisenceSequence,
    antisenceFiveMod: selectedOligoValues?.antisenceFiveMod,
    antisenceThreeMod: selectedOligoValues?.antisenceThreeMod,
    antisenceBaseCount: selectedOligoValues?.antisenceBaseCount,
    customerOverhang: selectedOligoValues?.customerOverhang,
    customerAntisenseOverHang: selectedOligoValues?.customerAntisenseOverHang,
  }

  const removeItemFromOligoList = (
    idToDelete: number | null = null,
    all: boolean = false,
    redirectToSequenceEntry: boolean = true,
    calledFromMultipleEntry: boolean = false
  ) => {
    formik.setErrors({})
    let activeLink = ActiveLinkType.SequenceEntry

    if (calledFromMultipleEntry) {
      activeLink = ActiveLinkType.MultipleEntry
    }

    const newQuery = {
      country: router.query.country,
      language: router.query.language,
      product: router.query.product,
      activeLink: activeLink,
    }

    if (formik?.values?.oligoList?.length === 1 || all === true) {
      if (redirectToSequenceEntry) {
        formik.setFieldValue('activeOligoIndex', 0, false)
        router
          .replace(
            {
              pathname: router.pathname,
              query: newQuery,
            },
            undefined,
            { shallow: false }
          )
          .then(() => {
            formik.resetForm({})
            formik.setErrors({})
            formik.setTouched({})
          })
      }
      formik.resetForm({})
      formik.setErrors({})
      formik.setTouched({})
      return []
    } else {
      const newList = formik?.values?.oligoList?.filter(
        (_, i) => i !== idToDelete
      )
      formik.setValues({
        ...formik.values,
        oligoList: newList,
        activeOligoIndex: newList.length === 0 ? 0 : newList.length - 1,
      })
      return newList
    }
  }

  const addItemToOligoList = (
    newItem: SingleTubeFormValues,
    cartItemIsBeingEdited = false
  ) => {
    let specifications = {}
    if (formik.values.applyToNextOligos) {
      specifications = {
        concentration: formik.values.oligoList[0]?.concentration,
        format: formik.values.oligoList[0]?.format,
        otherValue: formik.values.oligoList[0]?.otherValue,
        purification: formik.values.oligoList[0]?.purification,
        scale: formik.values.oligoList[0]?.scale,
        tubes: formik.values.oligoList[0]?.tubes,
        amount: formik.values.oligoList[0]?.amount,
        additionalServices:
          formik.values.oligoList[0]?.additionalServices || [],
      }
    }
    const newOLigo = {
      ...newItem,
      ...specifications,
      displayPageError: false,
      listErrors: [],
    }
    // item from cart inserted at top
    const updatedOligoList = cartItemIsBeingEdited
      ? [newOLigo, ...formik.values.oligoList].filter((oligo) => oligo.name)
      : [...formik.values.oligoList, newOLigo]

    formik.setErrors({})
    formik.setTouched({})
    formik.setValues({
      ...formik.values,
      oligoList: updatedOligoList,
      activeOligoIndex: updatedOligoList.length - 1,
      activeTab: 0,
      antisenseActiveTab: 0,
    })
  }
  const fieldsToTouch = [
    'concentration',
    'format',
    'name',
    'otherValue',
    'purification',
    'scale',
    'sequence',
    'tubes',
    'amount',
    'antisenceSequence',
    'antisenceName',
    'customerOverhang',
    'customerAntisenseOverHang',
  ]
  const touchRequiredFields = () => {
    fieldsToTouch.forEach((field) => {
      setFieldTouched(field, true, false)
    })
  }
  const fieldsErrorKeyMap = {
    antisencesequence: 'antisenceSequence',
    customerantisenseoverHang: 'customerAntisenseOverHang',
    customeroverhang: 'customerOverhang',
    antisensfivemod: 'antisenceFiveMod',
    antisensthreemod: 'antisenceThreeMod',
  }
  const insertInternalMod = (
    internalMod,
    newSequence?: string,
    isValidationOverridden?: boolean
  ) => {
    const modRegex = /\[BHQ[^\]]*\]/g
    const probeType = selectedOligoValues.probeType
    const sequence =
      newSequence !== undefined ? newSequence : selectedOligoValues.sequence
    if (probeType !== 'internalquench' && probeType !== 'dualquench') {
      return sequence
    }
    const sequenceWithoutInternalMod = !modRegex.test(sequence)
      ? sequence
      : sequence.replace(modRegex, '')
    const baseRegex = /(\[.*?\])|([ACGTRKHDYSBNMWVUI])/g
    let baseCount = 0
    let insertIndex = -1
    // Determine the correct position to insert the internal modification
    sequenceWithoutInternalMod.replace(baseRegex, (match, p1, p2, offset) => {
      baseCount++
      if (baseCount === 9 && insertIndex === -1) {
        insertIndex = offset + match.length
      }
      return match
    })

    // Insert the internal modification if the position is found and the sequence length is appropriate
    if (
      (insertIndex !== -1 &&
        baseCount >= 10 &&
        baseCount <= 50 &&
        probeType === 'internalquench') ||
      probeType === 'dualquench' ||
      (isValidationOverridden && baseCount > 50)
    ) {
      const sequenceWithInternalMod = `${sequenceWithoutInternalMod.slice(0, insertIndex)}${internalMod}${sequenceWithoutInternalMod.slice(insertIndex)}`
      setOligoFieldValue('sequence', sequenceWithInternalMod)
    }
    return sequence
  }

  // This is validating the sense strand but we don't want to disable the sense strand
  // for mirna. Antisense is filled out first. We may need to revisit this or add an
  // invalidAntisense check for mirna
  const isValidSense = () =>
    selectedOligoValues?.sequence &&
    selectedOligoValues?.name &&
    !errors?.sequence &&
    !errors?.name

  const isValidAntiSense = () =>
    selectedOligoValues?.antisenceName &&
    selectedOligoValues?.antisenceSequence &&
    !errors?.antisenceName &&
    !errors?.antisenceSequence

  return {
    allValues: formik.values,
    selectedOligoValues,
    touched,
    errors,
    setOligoFieldValue,
    setFieldTouched,
    setFieldError,
    validationFields,
    setValues: formik.setValues,
    resetForm: formik.resetForm,
    oligoFieldName,
    removeItemFromOligoList,
    addItemToOligoList,
    touchRequiredFields,
    setFieldValue: formik.setFieldValue,
    activeOligoIndex: oligoIndex,
    insertInternalMod,
    setErrors: formik.setErrors,
    setTouched: formik.setTouched,
    fieldsErrorKeyMap: fieldsErrorKeyMap,
    isValidSense,
    isValidAntiSense,
  }
}

export default useFormikHelper
